import { FUNNEL_EXPLORATION } from '../layouts/funnelExploration.layout'
import { RFM_CLUSTERING } from '../layouts/rfmClustering.layout'
import { USER_BEHAVIOR } from '../layouts/userBehavior.layout'

import {
  registerReportLayout,
  type ReportLayoutConfig,
  type ReportLayoutKey,
} from '@/features/reports'

// Contract 24 §8 `registerAudienceContributions` — the registry-inversion slice (ledger §A11):
// the feature pushes its layouts INTO 08; 08 imports nothing from
// here. RFM_CLUSTERING moved here as one atomic seed handoff with its bespoke treemap; 08 no
// longer carries a pending renderer for a component it cannot legally import.
// The native Audience routes register these data-only layouts from `beforeEnter`, preserving
// lazy chunk ownership while guaranteeing the registry entry exists before `ReportPage` renders.
const AUDIENCE_REPORT_LAYOUTS = {
  FUNNEL_EXPLORATION,
  RFM_CLUSTERING,
  USER_BEHAVIOR,
} as const satisfies Record<string, ReportLayoutConfig>

export type AudienceLayoutKey = keyof typeof AUDIENCE_REPORT_LAYOUTS

export const AUDIENCE_LAYOUT_KEYS = Object.keys(AUDIENCE_REPORT_LAYOUTS) as AudienceLayoutKey[]

/**
 * Register this feature's two layouts. Idempotent by identity (the registry no-ops a repeat
 * registration of the same config object), so a route-level `beforeEnter` may call it on
 * every navigation without a guard.
 *
 * @returns the combined unregister — test isolation only, never called in production.
 */
export function registerAudienceLayouts(
  register: (key: ReportLayoutKey, config: ReportLayoutConfig) => () => void = registerReportLayout,
): () => void {
  const undos = Object.entries(AUDIENCE_REPORT_LAYOUTS).map(([key, config]) =>
    register(key, config),
  )
  return () => {
    for (const undo of undos) undo()
  }
}
